/**
 * Cart drawer: open/close, quantity changes, line removal and section
 * re-rendering. Other components (e.g. product forms, quick add) trigger
 * a refresh by dispatching `cart:refresh` on `document`.
 */
class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('CartDrawer');
    if (!this.drawer) return;
    this.panel = this.drawer.querySelector('.cart-drawer__panel');
    this.triggers = document.querySelectorAll('[data-cart-trigger]');

    this.debounceTimers = new Map();
    this.updateQueues = new Map();

    this.bindTriggers();
    this.bindDrawerEvents();
    document.addEventListener('cart:refresh', (event) => this.refresh(event.detail || {}));
  }

  bindTriggers() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        if (trigger.dataset.cartType !== 'drawer') return;
        event.preventDefault();
        this.open();
      });
    });
  }

  bindDrawerEvents() {
    this.drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
    this.drawer.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-close]')) {
        this.close();
        return;
      }
      const removeBtn = event.target.closest('[data-cart-remove]');
      if (removeBtn) {
        const item = removeBtn.closest('[data-cart-item]');
        clearTimeout(this.debounceTimers.get(item.dataset.key));
        this.runUpdate(item.dataset.key, 0, item);
        return;
      }
      const decreaseBtn = event.target.closest('[data-quantity-decrease]');
      const increaseBtn = event.target.closest('[data-quantity-increase]');
      if (decreaseBtn || increaseBtn) {
        const item = event.target.closest('[data-cart-item]');
        const input = item.querySelector('[data-quantity-input]');
        const next = Math.max(0, Number(input.value) + (increaseBtn ? 1 : -1));
        input.value = next;
        this.queueUpdate(item.dataset.key, next, item);
      }
    });
    this.drawer.addEventListener('change', (event) => {
      if (!event.target.matches('[data-quantity-input]')) return;
      const item = event.target.closest('[data-cart-item]');
      clearTimeout(this.debounceTimers.get(item.dataset.key));
      this.runUpdate(item.dataset.key, Math.max(0, Number(event.target.value)), item);
    });
  }

  // Rapid clicks on +/- otherwise fire a fresh fetch + full-drawer refresh per
  // click; overlapping requests race and can leave the drawer showing a stale
  // quantity. Debounce per line and chain requests so only the latest
  // quantity is ever sent, one at a time.
  runUpdate(key, quantity, itemEl) {
    const previous = this.updateQueues.get(key) || Promise.resolve();
    const next = previous.then(() => this.updateLine(key, quantity, itemEl));
    this.updateQueues.set(key, next);
  }

  queueUpdate(key, quantity, itemEl) {
    clearTimeout(this.debounceTimers.get(key));
    this.debounceTimers.set(
      key,
      setTimeout(() => {
        this.debounceTimers.delete(key);
        this.runUpdate(key, quantity, itemEl);
      }, 350)
    );
  }

  open() {
    this.drawer.hidden = false;
    document.body.style.overflow = 'hidden';
    window.requestAnimationFrame(() => this.panel.querySelector('button, a')?.focus());
  }

  close() {
    this.drawer.hidden = true;
    document.body.style.overflow = '';
  }

  async updateLine(key, quantity, itemEl) {
    itemEl?.classList.add('is-removing');
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!response.ok) throw new Error('Cart update failed');
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { openDrawer: true } }));
    } catch (error) {
      itemEl?.classList.remove('is-removing');
      console.error(error);
    }
  }

  async refresh({ openDrawer = false } = {}) {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const fresh = doc.getElementById('CartDrawer');
      if (!fresh) return;
      this.drawer.innerHTML = fresh.innerHTML;
      this.panel = this.drawer.querySelector('.cart-drawer__panel');

      const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('is-hidden', cart.item_count === 0);
        el.classList.add('is-bumped');
        window.setTimeout(() => el.classList.remove('is-bumped'), 400);
      });

      if (openDrawer) this.open();
    } catch (error) {
      console.error(error);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => new CartDrawer());
